import {useState} from "react";
import {Box, Collapse, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {appStyles} from "../../../../AppStyles.ts";

// Report section data model — mirrors desktop ProfileSummary structure
interface ReportSection {
    title: string;
    paragraphs: string[];
    subsections?: ReportSubsection[];
}

interface ReportSubsection {
    heading: string;
    paragraphs: string[];
}

// TODO: Replace with API call to load summary report from AResourceProfileManager
function loadSampleReport(): ReportSection[] {
    return [
        {
            title: "Resource Overview",
            paragraphs: [
                "This resource record represents a multi-page document acquired during the active collection cycle. The artifact has been catalogued, hashed, and indexed against the canonical resource definition for its assigned type, and is currently available for analyst review.",
                "Initial ingestion completed without errors. All extracted frames, pages, and embedded media have been preserved alongside the original artifact and are accessible from the Content panels of this viewer.",
            ],
            subsections: [
                {
                    heading: "Content Highlights",
                    paragraphs: [
                        "Text extraction yielded a substantial body of natural-language content suitable for downstream word and phrase resolvers. Key terms have been promoted to the words index for cross-resource correlation.",
                        "Embedded metadata fields were detected and surfaced through the Metadata panel, including authoring information and timestamps that align with the document's stated provenance.",
                    ],
                },
                {
                    heading: "Pending Items",
                    paragraphs: [
                        "A small number of pages contain low-quality scans that may benefit from re-processing through the enrichment pipeline. These pages are flagged but do not block analyst review.",
                    ],
                },
            ],
        },
        {
            title: "Source & Provenance",
            paragraphs: [
                "The source of this resource has been recorded through the standard provenance pipeline and links back to the originating collection event. Chain-of-custody entries are maintained on the Provenance panel and reflect every handler and transformation applied since acquisition.",
                "No discrepancies were observed between the recorded source URL, the acquisition timestamp, and the hash of the artifact at the time of ingestion. The resource is considered authoritative for the associated intelligence references.",
            ],
        },
        {
            title: "Analyst Notes",
            paragraphs: [
                "Analyst notes for this resource indicate that it should remain associated with its current set of intelligence references and continue to be monitored for re-use across other collections. Any future re-acquisition of the same artifact should be deduplicated against this record.",
            ],
        },
    ];
}

function UXResourceSummaryPanel() {

    const report = loadSampleReport();
    const [expanded, setExpanded] = useState<Set<number>>(() => new Set(report.map((_, i) => i)));

    const allExpanded = expanded.size === report.length;

    const toggleSection = (idx: number) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    const toggleAll = () => {
        if (allExpanded) setExpanded(new Set());
        else setExpanded(new Set(report.map((_, i) => i)));
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            <Stack direction="row" padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title={allExpanded ? "Collapse All" : "Expand All"}>
                        <IconButton size="small" sx={appStyles.panelIconButton} onClick={toggleAll}>
                            {allExpanded
                                ? <UnfoldLessIcon sx={{fontSize: 18, color: "#F2F5FA"}} />
                                : <UnfoldMoreIcon sx={{fontSize: 18, color: "#F2F5FA"}} />}
                        </IconButton>
                    </Tooltip>
                    <Typography sx={appStyles.panelSectionTitle}>Summary Report</Typography>
                </Stack>
                <Stack direction="row" spacing={1} sx={{position: "absolute", right: 8}}>
                    <Tooltip title="Copy to Clipboard">
                        <IconButton size="small" sx={appStyles.panelIconButton}>
                            <ContentCopyIcon sx={{fontSize: 16, color: "#F2F5FA"}} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Print">
                        <IconButton size="small" sx={appStyles.panelIconButton}>
                            <PrintIcon sx={{fontSize: 16, color: "#F2F5FA"}} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export">
                        <IconButton size="small" sx={appStyles.panelIconButton}>
                            <FileDownloadIcon sx={{fontSize: 16, color: "#F2F5FA"}} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
            <Box sx={{
                flex: 1,
                overflow: "auto",
                px: 4,
                py: 3,
                mx: 2,
                mb: 2,
                mt: 1,
            }}>
                <Stack direction="column" spacing={4} sx={{maxWidth: 900, mx: "auto"}}>
                    {report.map((section, idx) => {
                        const isOpen = expanded.has(idx);
                        return (
                            <Stack key={idx} direction="column" spacing={1.5}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    onClick={() => toggleSection(idx)}
                                    sx={{
                                        borderBottom: "1px solid #a8b8c0",
                                        pb: 1,
                                        cursor: "pointer",
                                        userSelect: "none",
                                        "&:hover": {opacity: 0.85},
                                    }}
                                >
                                    {isOpen
                                        ? <ExpandMoreIcon sx={{fontSize: 22, color: "#a8b8c0"}} />
                                        : <ChevronRightIcon sx={{fontSize: 22, color: "#a8b8c0"}} />}
                                    <Box sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #02cfee",
                                    }} />
                                    <Typography sx={{
                                        color: "#a8b8c0",
                                        fontSize: 18,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                    }}>
                                        {section.title}
                                    </Typography>
                                </Stack>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <Stack direction="column" spacing={1.5}>
                                        {section.paragraphs.map((para, pIdx) => (
                                            <Typography key={pIdx} sx={{
                                                color: "#FFFFFF",
                                                fontSize: 13,
                                                fontWeight: 300,
                                                lineHeight: 1.7,
                                                textAlign: "justify",
                                            }}>
                                                {para}
                                            </Typography>
                                        ))}
                                        {section.subsections?.map((sub, sIdx) => (
                                            <Stack key={sIdx} direction="column" spacing={1} sx={{mt: 3}}>
                                                <Typography sx={{
                                                    color: "#FFFFFF",
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    textAlign: "left",
                                                    mb: 0.5,
                                                }}>
                                                    {sub.heading}
                                                </Typography>
                                                {sub.paragraphs.map((para, pIdx) => (
                                                    <Typography key={pIdx} sx={{
                                                        color: "#FFFFFF",
                                                        fontSize: 13,
                                                        fontWeight: 300,
                                                        lineHeight: 1.7,
                                                        textAlign: "justify",
                                                    }}>
                                                        {para}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Collapse>
                            </Stack>
                        );
                    })}
                </Stack>
            </Box>
        </Stack>
    );
}

export default UXResourceSummaryPanel;
